import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
  Stack,
} from "@mui/material";
import { useAuth } from "../../hooks/useAuth";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useStudents } from "../../hooks/useStudents";
import { useSubjects, useVocationalSubjects, useVocationalSubjectsByClassName, useSubjectMarksMappingByClassName } from "../../hooks/useSubjects";
import { useMarksheetTypeOptions, useMonthTypeOptions, shouldUseNAForMonthType } from "../enum/MarksheetTypeOptions";
import { SelectChangeEvent } from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SubjectIcon from "@mui/icons-material/Subject";
import { useTranslation } from "react-i18next";
import { AssessmentType } from "../../utils/gradeDisplay";
import type { GradeCategory } from "../../hooks/useMarkSheets";
import { useMarkSheets } from "../../hooks/useMarkSheets";

export interface SubjectMarkInput {
  subjectCode: string;
  subjectName: string;
  theoryMarks: number | string | null;
  practicalMarks: number | string | null;
  quarterlyWeightage?: number;
  halfYearlyWeightage?: number;
  originalPracticalMarks?: number;
}

export interface MarksheetFormData {
  id?: number;
  studentId: number;
  schoolId: number;
  classId: number;
  marksheetType: string;
  sessionYear: string;
  className: string;
  subjects: SubjectMarkInput[];
  monthType: string; // Required field
  marksheet_assessment?: Partial<Record<AssessmentType, GradeCategory>>;
}

interface Props {
  open: boolean;
  handleClose: () => void;
  initialData?: MarksheetFormData;
  onSubmit: (data: MarksheetFormData) => void | Promise<void>;
  isEdit?: boolean;
  /** When set, gates ANNUAL edit mapping for classes 3/4/6/7 (see MARKSHEET_MAIN_TEMPLATE_SCHOOL_CODE). */
  schoolCode?: string;
}

// Helper function to get marksheet type from sessionStorage (used for auto-selection)
const getStoredMarksheetType = (): string => {
  if (typeof sessionStorage !== 'undefined') {
    const storedType = sessionStorage.getItem("marksheetTypeFilter");
    return storedType || "QUARTERLY"; // Default to QUARTERLY if not stored
  }
  return "QUARTERLY";
};

/** Matches MarkSheetMarksManagement: always standard template / edit mapping for this school. */
const MARKSHEET_MAIN_TEMPLATE_SCHOOL_CODE = "462182";

const MarkSheetFormModal: React.FC<Props> = ({
  open,
  handleClose,
  initialData,
  onSubmit,
  isEdit,
  schoolCode = "",
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isStudent = user?.roles?.includes('STUDENT');
  const { selectedClass, selectedSession } = useSessionClass();
  const { useMarksheetByStudentId } = useMarkSheets(false, true);
  const marksheetTypeOptions = useMarksheetTypeOptions(selectedClass?.name);
  const monthTypeOptions = useMonthTypeOptions();
  const schoolId = user?.schoolId ?? 0;
  const classId = selectedClass?.id ?? 0;
  const className = selectedClass
    ? `${selectedClass.name} ${selectedClass.section}`
    : "";
  const sessionYear = selectedSession?.name ?? "";
  const selectedSessionId = selectedSession?.id;
  const { studentsQuery, useStudentsByClassId, useStudentsBySessionIdAndClassId } = useStudents();
  const { subjectsQuery } = useSubjects();
  const isAllClassForApi =
    selectedClass?.section === "__GRADE_ALL__" ||
    selectedClass?.name === "ALL" ||
    String(selectedClass?.name || "").includes("-ALL");

  const allClassNameForApi =
    !isAllClassForApi
      ? ""
      : String(selectedClass?.name || "").includes("-ALL")
        ? String(selectedClass?.name || "")
        : selectedClass?.name
          ? `${selectedClass.name}-ALL`
          : "";

  const vocationalByClassIdQuery = useVocationalSubjects(classId);
  const vocationalByClassNameQuery = useVocationalSubjectsByClassName(allClassNameForApi);
  const vocationalSubjects = (isAllClassForApi
    ? vocationalByClassNameQuery.data
    : vocationalByClassIdQuery.data);

  // Normalize IDs for the hook - ensure they are valid numbers or undefined
  const normalizedSessionId = selectedSessionId && selectedSessionId > 0 ? selectedSessionId : undefined;
  const normalizedClassId = classId && classId > 0 ? classId : undefined;

  // Use filtered API query when both session and class are selected, but disable for students
  const {
    data: filteredStudentsFromAPI = [],
    isLoading: isLoadingFilteredStudents
  } = useStudentsBySessionIdAndClassId(
    isStudent ? undefined : normalizedSessionId,
    isStudent ? undefined : normalizedClassId
  );

  // Fallback to class-based query when session is not selected, but disable for students
  const studentsByClassIdQuery = useStudentsByClassId(isStudent ? 0 : classId);

  // Also get all students as fallback
  const allStudents = studentsQuery.data || [];

  // Helper function to extract base class name (e.g., "10th" from "10th A" or "10th")
  const getBaseClassName = (className: string): string => {
    if (!className) return "";
    // Remove section part if present (e.g., "10th A" -> "10th", "6th C" -> "6th")
    // Match class name pattern like "10th", "6th", "12th", etc.
    const match = className.match(/^(\d+(?:th|st|nd|rd)?)/i);
    return match ? match[1] : className.split(/\s+/)[0]; // Fallback to first word
  };

  // Get base class name from selected class (without section)
  const baseClassName = selectedClass?.name ? getBaseClassName(selectedClass.name) : "";
  const lowerBaseClassName = baseClassName.toLowerCase();
  const isClass3rd4th6th7th =
    lowerBaseClassName.includes("3rd") ||
    lowerBaseClassName.includes("4th") ||
    lowerBaseClassName.includes("6th") ||
    lowerBaseClassName.includes("7th");

  const isMainTemplateSchool =
    String(schoolCode ?? "").trim() === MARKSHEET_MAIN_TEMPLATE_SCHOOL_CODE;

  // Fetch subject marks mapping (configured max theory/practical) for this class
  const { data: subjectMarksMapping } = useSubjectMarksMappingByClassName(
    baseClassName || undefined
  );

  // Build a lookup map by subject code for quick access to max marks
  const subjectMarksByCode = React.useMemo(() => {
    const map = new Map<string, any>();
    if (Array.isArray(subjectMarksMapping)) {
      subjectMarksMapping.forEach((m: any) => {
        if (m.subjectCode) {
          map.set(String(m.subjectCode).trim(), m);
        }
      });
    }
    return map;
  }, [subjectMarksMapping]);

  // Build a lookup map of subjects by code (for vocational detection, etc.)
  const subjectsByCode = React.useMemo(() => {
    const map = new Map<string, any>();
    (subjectsQuery.data || []).forEach((s: any) => {
      if (s?.subjectCode) {
        map.set(String(s.subjectCode).trim(), s);
      }
    });
    return map;
  }, [subjectsQuery.data]);

  // Filter subjects for current class by class name only (ignoring section)
  const allClassSubjects = React.useMemo(() => {
    const filtered = (subjectsQuery.data || []).filter((s) => {
      if (!s.className) return false;
      // Match by base class name (e.g., "10th" matches "10th", "10th A", "10th B", etc.)
      const subjectBaseClassName = getBaseClassName(s.className);
      return subjectBaseClassName === baseClassName;
    });

    return filtered;
  }, [subjectsQuery.data, baseClassName, selectedClass?.name, selectedClass?.section]);

  // Helper function to get student display name
  const getStudentDisplayName = (student: any): string => {
    if (student.studentName) {
      return student.studentName;
    } else if (student.firstName && student.lastName) {
      return `${student.firstName} ${student.lastName}`;
    } else if (student.firstName) {
      return student.firstName;
    } else if (student.lastName) {
      return student.lastName;
    } else {
      return 'Unknown Student';
    }
  };

  // Helper function to safely get student class ID
  const getStudentClassId = (student: any): number => {
    return student.schoolClassId || student.classId || 0;
  };

  // Helper function to safely get student session name
  const getStudentSessionName = (student: any): string => {
    return student.sessionName || '';
  };

  // Helper function to get selected student from a given students array
  const getSelectedStudent = (studentsArray: any[], studentId: number) => {
    return studentsArray.find(student => student.id === studentId);
  };

  // Filter subjects based on selected student's subjects
  const getStudentSubjects = (studentsArray: any[], studentId: number) => {
    const selectedStudent = getSelectedStudent(studentsArray, studentId);

    if (!selectedStudent || !selectedStudent.subjects) {
      return [];
    }

    // Handle both string and array formats for subjects
    let studentSubjectCodes: string[] = [];
    if (Array.isArray(selectedStudent.subjects)) {
      studentSubjectCodes = selectedStudent.subjects;
    } else if (typeof selectedStudent.subjects === 'string') {
      studentSubjectCodes = selectedStudent.subjects.split(',').map((s: string) => s.trim()).filter(Boolean);
    }

    // Filter class subjects to only include those chosen by the student
    const filteredSubjects = allClassSubjects.filter(subject =>
      studentSubjectCodes.includes(subject.subjectCode)
    );

    return filteredSubjects;
  };

  // Filter and sort students by class and session
  // Use filtered API when both session and class are selected, otherwise filter manually
  const filteredStudents = React.useMemo(() => {
    // If both session and class are selected, use the filtered API results
    if (normalizedSessionId && normalizedClassId) {
      return (filteredStudentsFromAPI || [])
        .filter((student) => student.status !== false)
        .sort((a, b) => {
          const nameA = getStudentDisplayName(a);
          const nameB = getStudentDisplayName(b);
          return nameA.localeCompare(nameB);
        });
    }

    // Otherwise, use the old filtering logic for backward compatibility
    if (!selectedSession?.id || !classId) {
      return [];
    }

    // Try both approaches - useStudentsByClassId and manual filtering
    let studentsToFilter = studentsByClassIdQuery.data || [];

    // If useStudentsByClassId doesn't return data, try filtering all students
    if (studentsToFilter.length === 0 && allStudents.length > 0) {
      studentsToFilter = allStudents;
    }

    // Filter by both class and session, and exclude students with status false
    let students = studentsToFilter.filter(student => {
      const studentClassId = getStudentClassId(student);
      const classMatch = studentClassId === classId;
      const sessionMatch = student.sessionId === selectedSession.id;
      const isActive = student.status !== false;

      return classMatch && sessionMatch && isActive;
    });

    // Sort alphabetically by name (A to Z)
    students.sort((a, b) => {
      const nameA = getStudentDisplayName(a);
      const nameB = getStudentDisplayName(b);
      return nameA.localeCompare(nameB);
    });

    return students;
  }, [
    normalizedSessionId,
    normalizedClassId,
    filteredStudentsFromAPI,
    studentsByClassIdQuery.data,
    allStudents,
    selectedSession?.id,
    classId
  ]);

  // Initial form state — school 462182: 3/4/6/7 add flow defaults to QUARTERLY; other schools use HALF_YEARLY.
  const initialMarksheetType =
    initialData?.marksheetType ||
    (isClass3rd4th6th7th
      ? isMainTemplateSchool
        ? "QUARTERLY"
        : "HALF_YEARLY"
      : getStoredMarksheetType());
  const initialMonthType = initialData?.monthType || (shouldUseNAForMonthType(initialMarksheetType) ? "NA" : "JANUARY");

  const allowedAssessmentGradeOptions: Array<{ value: GradeCategory; label: string }> = [
    { value: "A_P", label: "A+" },
    { value: "A", label: "A" },
    { value: "B_P", label: "B+" },
    { value: "B", label: "B" },
    { value: "C_P", label: "C+" },
    { value: "C", label: "C" },
    { value: "D", label: "D" },
    { value: "E", label: "E" },
  ];

  const coScholasticAssessments: Array<{ key: AssessmentType; label: string }> = [
    { key: AssessmentType.SAHITYIK, label: "साहित्यिक" },
    { key: AssessmentType.SANSKRITIK, label: "सांस्कृतिक" },
    { key: AssessmentType.VAIGYANIK, label: "वैज्ञानिक" },
    { key: AssessmentType.SRIJANATMAK, label: "सृजनात्मक" },
    { key: AssessmentType.KHELKUD, label: "खेलकूद" },
  ];

  const personalSocialAssessments: Array<{ key: AssessmentType; label: string }> = [
    { key: AssessmentType.REGULARITY, label: "नियमितता एवं समयबद्धता" },
    { key: AssessmentType.CLEANLINESS, label: "स्वच्छता" },
    { key: AssessmentType.DISCIPLINE, label: "अनुशासन / कर्तव्यनिष्ठा" },
    { key: AssessmentType.COOPERATION, label: "सहयोग की भावना / अभिरुचि" },
    { key: AssessmentType.ENVIRONMENT, label: "पर्यावरण के प्रति संवेदनशीलता" },
    { key: AssessmentType.LEADERSHIP, label: "नेतृत्व की क्षमता" },
    { key: AssessmentType.HONESTY, label: "सत्यवादिता एवं ईमानदारी" },
  ];

  const setAssessmentGrade = (key: AssessmentType, value: GradeCategory) => {
    setForm((prev) => ({
      ...prev,
      marksheet_assessment: {
        ...(prev.marksheet_assessment || {}),
        [key]: value,
      },
    }));
  };

  const [form, setForm] = useState<MarksheetFormData>({
    studentId: 0, // Will be set properly in useEffect
    schoolId: schoolId,
    classId: classId,
    marksheetType: initialMarksheetType,
    sessionYear: sessionYear,
    className: className,
    monthType: initialMonthType,
    subjects: [], // Will be populated when student is selected
    id: initialData?.id,
    marksheet_assessment: (initialData as any)?.marksheet_assessment || {},
  });

  // Add state for form errors
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSelectionKeyRef = useRef<string>("");
  const lastAutofillAppliedKeyRef = useRef<string>("");

  const shouldShowAssessmentGrades =
    isClass3rd4th6th7th && (form.marksheetType === "ANNUAL" || form.marksheetType === "FINAL");


  // Update form when modal opens or initialData changes (but not when studentId changes)
  useEffect(() => {
    if (!open) return;

    const selectedStudentId = initialData?.studentId ?? (filteredStudents.length > 0 ? filteredStudents[0].id : 0);
    const currentSubjects = getStudentSubjects(filteredStudents, selectedStudentId);

    // Check for specific class/marksheet combinations
    const lowerBaseClass = baseClassName.toLowerCase();
    const is9thClass = lowerBaseClass.includes("9th");
    const is6thOr7th = lowerBaseClass.includes("6th") || lowerBaseClass.includes("7th");
    const isAnnual = (initialData?.marksheetType || getStoredMarksheetType()) === "ANNUAL";

    const marksheetType =
      initialData?.marksheetType ||
      (isClass3rd4th6th7th
        ? isMainTemplateSchool
          ? "QUARTERLY"
          : "HALF_YEARLY"
        : getStoredMarksheetType());
    const monthType = initialData?.monthType || (shouldUseNAForMonthType(marksheetType) ? "NA" : "JANUARY");

    setForm({
      studentId: selectedStudentId,
      schoolId: schoolId,
      classId: classId,
      marksheetType: marksheetType,
      sessionYear: sessionYear,
      className: className,
      monthType: monthType,
      marksheet_assessment: (initialData as any)?.marksheet_assessment || {},
      subjects: currentSubjects.map((sub) => {
        const existingSubject = initialData?.subjects?.find(
          (sm) => sm.subjectCode === sub.subjectCode
        );

        const quarterlyWeightage = existingSubject?.quarterlyWeightage;
        const halfYearlyWeightage = existingSubject?.halfYearlyWeightage;

        // Class 6th/7th ANNUAL: school 462182 keeps legacy mapping (theory column ↔ originalPracticalMarks).
        if (isMainTemplateSchool && is6thOr7th && isAnnual && existingSubject) {
          return {
            subjectCode: sub.subjectCode,
            subjectName: sub.name,
            theoryMarks: existingSubject.originalPracticalMarks ?? null,
            practicalMarks: existingSubject.practicalMarks ?? null,
            quarterlyWeightage: quarterlyWeightage,
            halfYearlyWeightage: halfYearlyWeightage,
          };
        }

        // Other schools: classes 3/4/6/7 ANNUAL — theory from API theory; practical column shows originalPracticalMarks.
        if (
          !isMainTemplateSchool &&
          isClass3rd4th6th7th &&
          isAnnual &&
          existingSubject
        ) {
          const practDisplay =
            existingSubject.originalPracticalMarks !== undefined &&
            existingSubject.originalPracticalMarks !== null
              ? existingSubject.originalPracticalMarks
              : (existingSubject.practicalMarks ?? null);
          return {
            subjectCode: sub.subjectCode,
            subjectName: sub.name,
            theoryMarks: existingSubject.theoryMarks ?? null,
            practicalMarks: practDisplay,
            quarterlyWeightage: quarterlyWeightage,
            halfYearlyWeightage: halfYearlyWeightage,
          };
        }

        // Existing logic for 9th class ANNUAL
        let practicalMarks = null;
        if (is9thClass && isAnnual && existingSubject) {
          if (existingSubject.originalPracticalMarks !== null && existingSubject.originalPracticalMarks !== undefined) {
            practicalMarks = existingSubject.originalPracticalMarks;
          } else if (existingSubject.practicalMarks !== null && existingSubject.practicalMarks !== undefined && existingSubject.practicalMarks !== -1) {
            practicalMarks = existingSubject.practicalMarks;
          }
        } else {
          practicalMarks = existingSubject?.practicalMarks ?? null;
        }

        return {
          subjectCode: sub.subjectCode,
          subjectName: sub.name,
          theoryMarks: existingSubject?.theoryMarks ?? null,
          practicalMarks: practicalMarks,
          quarterlyWeightage: quarterlyWeightage,
          halfYearlyWeightage: halfYearlyWeightage,
        };
      }),
      id: initialData?.id,
    });
    // eslint-disable-next-line
  }, [open, initialData, schoolId, sessionYear, className, classId, schoolCode]);

  // Update subjects when student changes (only for new marksheets, not when editing)
  useEffect(() => {
    if (form.studentId && !initialData && !isEdit) {
      const currentSubjects = getStudentSubjects(filteredStudents, form.studentId);
      setForm(prev => ({
        ...prev,
        subjects: currentSubjects.map((sub) => ({
          subjectCode: sub.subjectCode,
          subjectName: sub.name,
          theoryMarks: null,
          practicalMarks: null,
        })),
      }));
    }
  }, [form.studentId, initialData, isEdit]);

  const shouldAutofillExistingMarksheet = open && !isEdit && !initialData;
  const studentMarksheetsQuery = useMarksheetByStudentId(
    shouldAutofillExistingMarksheet ? form.studentId : null,
    shouldAutofillExistingMarksheet
  );

  // When selection changes (student + type + month), clear stale marks first (add mode only).
  // Then the autofill effect (below) can re-populate if an existing marksheet is found.
  useEffect(() => {
    if (!shouldAutofillExistingMarksheet) return;
    if (!form.studentId) return;
    if (!classId || !selectedSessionId) return;

    const selectionKey = [
      form.studentId,
      form.marksheetType,
      form.marksheetType === "MONTHLY" ? (form.monthType || "JANUARY") : "NA",
      classId,
      selectedSessionId,
    ].join("|");

    if (lastSelectionKeyRef.current === selectionKey) return;
    lastSelectionKeyRef.current = selectionKey;
    lastAutofillAppliedKeyRef.current = "";

    const currentSubjects = getStudentSubjects(filteredStudents, form.studentId);
    setForm((prev) => ({
      ...prev,
      // keep selection fields as-is, just clear values to avoid carry-over
      marksheet_assessment: {},
      subjects: currentSubjects.map((sub) => ({
        subjectCode: sub.subjectCode,
        subjectName: sub.name,
        theoryMarks: null,
        practicalMarks: null,
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldAutofillExistingMarksheet,
    open,
    form.studentId,
    form.marksheetType,
    form.monthType,
    classId,
    selectedSessionId,
    filteredStudents,
  ]);

  // Autofill marks if a marksheet already exists for this selection (student + type + month + class + session).
  useEffect(() => {
    if (!shouldAutofillExistingMarksheet) return;
    if (!form.studentId) return;
    if (!classId || !selectedSessionId) return;

    const key = [
      form.studentId,
      form.marksheetType,
      form.marksheetType === "MONTHLY" ? (form.monthType || "JANUARY") : "NA",
      classId,
      selectedSessionId,
    ].join("|");

    // Avoid repeatedly reapplying the same autofill.
    if (lastAutofillAppliedKeyRef.current === key) return;

    const marksheets = studentMarksheetsQuery.data || [];
    if (!Array.isArray(marksheets) || marksheets.length === 0) return;

    const effectiveMonth =
      form.marksheetType === "MONTHLY" ? (form.monthType || "JANUARY") : "NA";

    const existing = marksheets.find((ms: any) => {
      const typeMatch = String(ms?.marksheetType || "") === String(form.marksheetType || "");
      const monthMatch =
        form.marksheetType === "MONTHLY"
          ? String(ms?.monthType || "") === String(effectiveMonth)
          : true;
      const classMatch = Number(ms?.classId || 0) === Number(classId);
      const sessionMatch = Number(ms?.sessionId || 0) === Number(selectedSessionId);
      return typeMatch && monthMatch && classMatch && sessionMatch;
    });

    if (!existing) return;

    const currentSubjects = getStudentSubjects(filteredStudents, form.studentId);

    // Apply the same subject mapping rules used in edit flow (initialData).
    const lowerBaseClass = baseClassName.toLowerCase();
    const is9thClass = lowerBaseClass.includes("9th");
    const is6thOr7th = lowerBaseClass.includes("6th") || lowerBaseClass.includes("7th");
    const isAnnual = String(form.marksheetType) === "ANNUAL";

    lastAutofillAppliedKeyRef.current = key;
    setForm((prev) => ({
      ...prev,
      // keep studentId/marksheetType/monthType as-is; just fill marks/assessment/id when found
      id: Number(existing?.id) || prev.id,
      marksheet_assessment: (existing as any)?.marksheet_assessment || prev.marksheet_assessment || {},
      subjects: currentSubjects.map((sub) => {
        const existingSubject = (existing?.subjects || []).find(
          (sm: any) => sm.subjectCode === sub.subjectCode
        );

        const quarterlyWeightage = existingSubject?.quarterlyWeightage;
        const halfYearlyWeightage = existingSubject?.halfYearlyWeightage;

        // Class 6th/7th ANNUAL: school 462182 keeps legacy mapping (theory column ↔ originalPracticalMarks).
        if (isMainTemplateSchool && is6thOr7th && isAnnual && existingSubject) {
          return {
            subjectCode: sub.subjectCode,
            subjectName: sub.name,
            theoryMarks: existingSubject.originalPracticalMarks ?? null,
            practicalMarks: existingSubject.practicalMarks ?? null,
            quarterlyWeightage,
            halfYearlyWeightage,
          };
        }

        // Other schools: classes 3/4/6/7 ANNUAL — theory from API theory; practical column shows originalPracticalMarks.
        if (
          !isMainTemplateSchool &&
          isClass3rd4th6th7th &&
          isAnnual &&
          existingSubject
        ) {
          const practDisplay =
            existingSubject.originalPracticalMarks !== undefined &&
            existingSubject.originalPracticalMarks !== null
              ? existingSubject.originalPracticalMarks
              : (existingSubject.practicalMarks ?? null);
          return {
            subjectCode: sub.subjectCode,
            subjectName: sub.name,
            theoryMarks: existingSubject.theoryMarks ?? null,
            practicalMarks: practDisplay,
            quarterlyWeightage,
            halfYearlyWeightage,
          };
        }

        // Existing logic for 9th class ANNUAL
        let practicalMarks = null;
        if (is9thClass && isAnnual && existingSubject) {
          if (
            existingSubject.originalPracticalMarks !== null &&
            existingSubject.originalPracticalMarks !== undefined
          ) {
            practicalMarks = existingSubject.originalPracticalMarks;
          } else if (
            existingSubject.practicalMarks !== null &&
            existingSubject.practicalMarks !== undefined &&
            existingSubject.practicalMarks !== -1
          ) {
            practicalMarks = existingSubject.practicalMarks;
          }
        } else {
          practicalMarks = existingSubject?.practicalMarks ?? null;
        }

        return {
          subjectCode: sub.subjectCode,
          subjectName: sub.name,
          theoryMarks: existingSubject?.theoryMarks ?? null,
          practicalMarks,
          quarterlyWeightage,
          halfYearlyWeightage,
        };
      }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldAutofillExistingMarksheet,
    open,
    form.studentId,
    form.marksheetType,
    form.monthType,
    classId,
    selectedSessionId,
    studentMarksheetsQuery.data,
    filteredStudents,
    baseClassName,
    isMainTemplateSchool,
    isClass3rd4th6th7th,
  ]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;

    // Auto-set monthType to "NA" when non-MONTHLY marksheet types are selected
    let updatedMonthType: string | undefined = undefined;
    if (name === "marksheetType") {
      if (shouldUseNAForMonthType(value)) {
        updatedMonthType = "NA";
      } else if (value === "MONTHLY" && form.monthType === "NA") {
        // Reset to default if switching from NA marksheet type to MONTHLY
        updatedMonthType = "JANUARY";
      }
    }

    setForm((f) => ({
      ...f,
      [name!]: name === "studentId" ? Number(value) : value,
      ...(updatedMonthType !== undefined ? { monthType: updatedMonthType } : {}),
      ...(name === "marksheetType" &&
      !isMainTemplateSchool &&
      isClass3rd4th6th7th &&
      value === "HALF_YEARLY"
        ? {
            // Half yearly for 3/4/6/7 (non–462182): theory-only; clear practical inputs
            subjects: f.subjects.map((sm) => ({ ...sm, practicalMarks: null })),
          }
        : {}),
    }));
  };

  const handleSubjectMarkChange = (
    idx: number,
    field: "theoryMarks" | "practicalMarks",
    value: string | number
  ) => {
    // Handle different input types:
    // - Empty string '' → null (not filled, will be converted to -1 in payload)
    // - String '0' → 0 (user entered 0)
    // - Number 0 → 0 (user entered 0)
    // - Other numbers → keep as number
    let processedValue: string | number | null;

    if (value === '') {
      processedValue = null; // Empty field
    } else if (value === '0' || value === 0) {
      processedValue = 0; // User explicitly entered 0
    } else {
      processedValue = value; // Other values
    }

    setForm((f) => ({
      ...f,
      subjects: f.subjects.map((sm, i) =>
        i === idx ? { ...sm, [field]: processedValue } : sm
      ),
    }));

    // Only validate if there's a non-empty value
    if (processedValue !== null && processedValue !== undefined && processedValue !== '') {
      const numericValue = Number(processedValue);
      const subjectName = form.subjects[idx]?.subjectName || '';
      const subjectCode = form.subjects[idx]?.subjectCode || '';
      const maxMarksConfig = getCustomMaxMarks(subjectName, form.marksheetType, subjectCode);
      const maxMarks = field === "theoryMarks" ? maxMarksConfig.theory : maxMarksConfig.practical;
      const errorKey = `${field}_${idx}`;

      if (numericValue > maxMarks) {
        setFormErrors(prev => ({
          ...prev,
          [errorKey]: t("marks_cannot_exceed").replace('{maxMarks}', maxMarks.toString())
        }));
      } else if (numericValue < 0) {
        setFormErrors(prev => ({
          ...prev,
          [errorKey]: t("marks_cannot_be_negative")
        }));
      } else {
        // Clear error if validation passes
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        });
      }
    } else {
      // Clear error if field is empty
      const errorKey = `${field}_${idx}`;
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const effectiveMarksheetType =
      form.marksheetType === "FINAL" ? "ANNUAL" : form.marksheetType;

    // Only validate marks that are actually entered (non-empty)
    let hasErrors = false;
    const newErrors: { [key: string]: string } = {};

    form.subjects.forEach((subject, idx) => {
      // Handle null values properly - don't convert to numbers if they're null
      const theoryMarks = subject.theoryMarks === null || subject.theoryMarks === '' ?
        null : Number(subject.theoryMarks);
      const practicalMarks = subject.practicalMarks === null || subject.practicalMarks === '' ?
        null : Number(subject.practicalMarks);

      // Get custom max marks configuration for this subject
      const maxMarksConfig = getCustomMaxMarks(
        subject.subjectName,
        effectiveMarksheetType,
        subject.subjectCode
      );

      // Only validate if marks are actually entered (not empty)
      if (theoryMarks !== null && theoryMarks !== undefined && !isNaN(Number(theoryMarks))) {
        if (theoryMarks > maxMarksConfig.theory) {
          newErrors[`theoryMarks_${idx}`] = t("theory_marks_cannot_exceed").replace('{maxMarks}', maxMarksConfig.theory.toString());
          hasErrors = true;
        }
        if (theoryMarks < 0) {
          newErrors[`theoryMarks_${idx}`] = t("theory_marks_cannot_be_negative");
          hasErrors = true;
        }
      }

      if (practicalMarks !== null && practicalMarks !== undefined && !isNaN(Number(practicalMarks))) {
        if (effectiveMarksheetType !== "MONTHLY") {
          // Non-Monthly validation (Theory/Practical)
          if (practicalMarks > maxMarksConfig.practical) {
            newErrors[`practicalMarks_${idx}`] = t("practical_marks_cannot_exceed").replace('{maxMarks}', maxMarksConfig.practical.toString());
            hasErrors = true;
          }
          if (practicalMarks < 0) {
            newErrors[`practicalMarks_${idx}`] = t("practical_marks_cannot_be_negative");
            hasErrors = true;
          }
        }
      }
    });

    if (hasErrors) {
      setFormErrors(newErrors);
      return; // Don't submit if there are validation errors
    }

    // Check for specific class/marksheet combinations
    const lowerBaseClass = baseClassName.toLowerCase();
    const is6thOr7th = lowerBaseClass.includes("6th") || lowerBaseClass.includes("7th");
    const isAnnual = effectiveMarksheetType === "ANNUAL";

    // Process subjects to send -1 for empty fields and 0 for user-entered zeros
    const processedSubjects = form.subjects.map(subject => {
      // School 462182 + 6th/7th ANNUAL: legacy mapping (theory UI → originalPracticalMarks + theoryMarks).
      if (isMainTemplateSchool && is6thOr7th && isAnnual) {
        return {
          subjectCode: subject.subjectCode,
          subjectName: subject.subjectName,
          originalPracticalMarks: subject.theoryMarks === null ? -1 : Number(subject.theoryMarks),
          practicalMarks: subject.practicalMarks === null ? -1 : Number(subject.practicalMarks),
          theoryMarks: subject.theoryMarks === null ? -1 : Number(subject.theoryMarks),
        };
      }

      // Other schools + 3/4/6/7 ANNUAL: practical UI column is originalPracticalMarks; theory is theoryMarks.
      if (!isMainTemplateSchool && isClass3rd4th6th7th && isAnnual) {
        const th = subject.theoryMarks === null || subject.theoryMarks === "" ? -1 : Number(subject.theoryMarks);
        const pr = subject.practicalMarks === null || subject.practicalMarks === "" ? -1 : Number(subject.practicalMarks);
        return {
          subjectCode: subject.subjectCode,
          subjectName: subject.subjectName,
          theoryMarks: th,
          practicalMarks: pr,
          originalPracticalMarks: pr,
        };
      }

      return {
        subjectCode: subject.subjectCode,
        subjectName: subject.subjectName,
        theoryMarks: subject.theoryMarks === null ? -1 : Number(subject.theoryMarks),
        practicalMarks: subject.practicalMarks === null ? -1 : Number(subject.practicalMarks),
      };
    });

    const submissionData = {
      ...form,
      id: initialData?.id,
      studentId: Number(form.studentId),
      schoolId,
      classId,
      sessionYear,
      className,
      monthType: form.monthType || 'JANUARY', // Ensure monthType is always present
      marksheetType: effectiveMarksheetType,
      subjects: processedSubjects,
    };

    try {
      setIsSubmitting(true);
      await Promise.resolve(onSubmit(submissionData));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setFormErrors({}); // Clear all errors when closing
    handleClose();
  };

  // Helper function to check if subject needs custom max marks
  const getCustomMaxMarks = (
    subjectName: string,
    marksheetType: string,
    subjectCode?: string
  ) => {
    // For monthly marksheets, use fixed max marks regardless of subject
    if (marksheetType === "MONTHLY") {
      const monthlyMaxFor347 =
        isClass3rd4th6th7th && isMainTemplateSchool
          ? 20
          : isClass3rd4th6th7th
            ? 10
            : 20;
      return { theory: monthlyMaxFor347, practical: 0 };
    }

    // Check if subject is vocational using API data (name) + fallback by code/category
    const normalizedSubjectName = subjectName.toLowerCase().trim();
    const isVocationalByName = vocationalSubjects?.has(normalizedSubjectName) ?? false;

    const normalizedSubjectCode = subjectCode ? String(subjectCode).trim() : "";
    const subjectFromCode = normalizedSubjectCode ? subjectsByCode.get(normalizedSubjectCode) : undefined;
    const subjectCategory = String(subjectFromCode?.subjectCategory ?? "").toLowerCase().trim();
    const normalizedNameFromCode = String(subjectFromCode?.name ?? "").toLowerCase().trim();
    const isVocationalByCategory = !!subjectCategory && subjectCategory.includes("vocational");
    const isVocationalByCodeName =
      !!normalizedNameFromCode && (vocationalSubjects?.has(normalizedNameFromCode) ?? false);

    const isVocationalSubject = isVocationalByName || isVocationalByCategory || isVocationalByCodeName;

    // Vocational subjects always use 40:60 (ignore mappings / reductions)
    if (isVocationalSubject) {
      return { theory: 40, practical: 60 };
    }

    // First, try to use SubjectMarksMapping config (by subject code)
    if (subjectCode) {
      const mapping = subjectMarksByCode.get(String(subjectCode).trim());
      if (mapping) {
        const mappedTheory =
          mapping.theoryMarks !== null && mapping.theoryMarks !== undefined
            ? Number(mapping.theoryMarks)
            : undefined;
        let mappedPractical =
          mapping.practicalMarks !== null && mapping.practicalMarks !== undefined
            ? Number(mapping.practicalMarks)
            : undefined;

        // Special case: 9th class with ANNUAL marksheet type
        const is9thClass = baseClassName.toLowerCase().includes("9th");
        const isAnnual = marksheetType === "ANNUAL";

        if (is9thClass && isAnnual && mappedPractical !== undefined) {
          mappedPractical = mappedPractical - 10;
        }

        if (mappedTheory !== undefined || mappedPractical !== undefined) {
          return {
            theory: mappedTheory !== undefined ? mappedTheory : 0,
            practical: mappedPractical !== undefined ? mappedPractical : 0,
          };
        }
      }
    }

    // Special case: 9th class with ANNUAL marksheet type
    const is9thClass = baseClassName.toLowerCase().includes("9th");
    const isAnnual = marksheetType === "ANNUAL";

    if (is9thClass && isAnnual) {
      return { theory: 75, practical: 15 };
    }

    // Default behavior for other cases
    // Default max marks for non-vocational subjects
    return { theory: 75, practical: 25 };
  };

  const getFilteredMarksheetOptions = (className: string) => {
    const isBoardClass =
      className.includes("10th") || className.includes("12th");

    return marksheetTypeOptions.filter((opt) => {
      if (isBoardClass) {
        return opt.value !== "ANNUAL"; // hide Annual for 10th and 12th
      } else {
        // hide Board for other classes
        if (opt.value === "BOARD" || opt.value === "PRE_BOARD") return false;
        // 3/4/6/7: hide QUARTERLY except for school 462182
        if (!isMainTemplateSchool && isClass3rd4th6th7th && opt.value === "QUARTERLY")
          return false;
        return true;
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleModalClose();
        }
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: '#3092e3',
          color: 'white',
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 3,
          px: 4,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {isEdit ? t("edit_marksheet") : t("add_marksheet")}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {isEdit ? t("update_student_marksheet_details") : t("create_new_student_marksheet")}
          </Typography>
        </Box>
        <IconButton
          onClick={handleModalClose}
          size="small"
          sx={{
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <DialogContent sx={{ p: 4, bgcolor: '#f8fafc', flex: 1, overflow: 'auto' }}>
          {/* Student Information Section */}
          <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <PersonIcon sx={{ color: "#3092e3" }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: "#3092e3" }}>
                  {t("student_information")}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      {t('student')}
                    </InputLabel>
                    <Select
                      value={form.studentId || ''}
                      onChange={(e) => {
                        const newStudentId = Number(e.target.value);
                        const selectedStudent = filteredStudents.find(s => s.id === newStudentId);
                        const studentSubjects = selectedStudent ?
                          allClassSubjects.filter(subject => {
                            let studentSubjectCodes: string[] = [];
                            if (Array.isArray(selectedStudent.subjects)) {
                              studentSubjectCodes = selectedStudent.subjects;
                            } else if (typeof selectedStudent.subjects === 'string') {
                              studentSubjectCodes = selectedStudent.subjects.split(',').map((s: string) => s.trim()).filter(Boolean);
                            }
                            return studentSubjectCodes.includes(subject.subjectCode);
                          }) : [];

                        // For edit mode, preserve existing marks; for add mode, reset to 0
                        const preserveMarks = isEdit && initialData;

                        setForm(prev => ({
                          ...prev,
                          studentId: newStudentId,
                          subjects: studentSubjects.map(sub => {
                            if (preserveMarks) {
                              // Try to find existing marks for this subject
                              const existingSubject = prev.subjects.find(ps => ps.subjectCode === sub.subjectCode);
                              return {
                                subjectCode: sub.subjectCode,
                                subjectName: sub.name,
                                theoryMarks: existingSubject?.theoryMarks ?? null,
                                practicalMarks: existingSubject?.practicalMarks ?? null,
                              };
                            } else {
                              return {
                                subjectCode: sub.subjectCode,
                                subjectName: sub.name,
                                theoryMarks: null,
                                practicalMarks: null,
                              };
                            }
                          })
                        }));
                      }}
                      label={t('student')}
                      disabled={!selectedClass || !selectedSession || isEdit}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            zIndex: 9999,
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                    >
                      {(() => {
                        // Determine which loading/error state to use based on whether we're using filtered API
                        const shouldUseFilteredQuery = !!(normalizedSessionId && normalizedClassId);

                        const isLoadingStudents = shouldUseFilteredQuery
                          ? isLoadingFilteredStudents
                          : (studentsByClassIdQuery.isLoading || studentsQuery.isLoading);

                        const hasError = shouldUseFilteredQuery
                          ? false // Filtered API error handling is done via empty data
                          : (studentsByClassIdQuery.isError || studentsQuery.isError);

                        if (isLoadingStudents) {
                          return <MenuItem disabled>{t('loading')}</MenuItem>;
                        }
                        if (hasError) {
                          return <MenuItem disabled>{t('error_loading_students')}</MenuItem>;
                        }
                        if (!selectedClass) {
                          return <MenuItem disabled>{t('no_class_selected_first')}</MenuItem>;
                        }
                        if (!selectedSession) {
                          return <MenuItem disabled>{t('no_session_selected_first')}</MenuItem>;
                        }
                        if (filteredStudents.length === 0) {
                          return (
                            <MenuItem disabled>
                              {t('no_students_found_for_class_session').replace('{className}', selectedClass.name).replace('{sessionName}', selectedSession.name)}
                            </MenuItem>
                          );
                        }
                        return null;
                      })()}
                      {filteredStudents.length > 0 &&
                        filteredStudents.map((student) => {
                          const studentName = getStudentDisplayName(student);
                          return (
                            <MenuItem key={student.id} value={student.id}>
                              <Box display="flex" alignItems="center" gap={2}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#0168EA' }}>
                                  {studentName.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {studentName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Roll: {student.rollNumber}
                                  </Typography>
                                </Box>
                              </Box>
                            </MenuItem>
                          );
                        })
                      }
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("class")}
                    value={className}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      startAdornment: <SchoolIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f5f5f5'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Marksheet Details Section */}
          <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <AssessmentIcon sx={{ color: "#3092e3" }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: "#3092e3" }}>
                  {t("marksheet_details")}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {t("marksheet_type")}
                    </InputLabel>
                    <Select
                      name="marksheetType"
                      value={form.marksheetType}
                      onChange={(e) => handleChange(e as any)}
                      required
                      label={t("marksheet_type")}
                      disabled={isEdit}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            zIndex: 9999,
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                    >
                      {getFilteredMarksheetOptions(className).map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Typography>{opt.label}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Month Type field - only show for MONTHLY marksheet type */}
                {form.marksheetType === "MONTHLY" && (
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>
                        {t("month")}
                      </InputLabel>
                      <Select
                        name="monthType"
                        value={form.monthType || "JANUARY"}
                        onChange={(e) => handleChange(e as any)}
                        required
                        label={t("month")}
                        disabled={isEdit}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              zIndex: 9999,
                            },
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                      >
                        {monthTypeOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            <Typography>{opt.label}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} md={4}>
                  <TextField
                    label={t("session_year")}
                    value={sessionYear}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      startAdornment: <SchoolIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f5f5f5'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {/* Subjects and Marks Section */}
          <Card sx={{ mt: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <SubjectIcon sx={{ color: "#3092e3" }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: "#3092e3" }}>
                  {t("subjects_and_marks")}
                </Typography>
              </Box>

              {form.subjects.length > 0 ? (
                form.subjects.map((sm, idx) => (
                  <Box
                    key={sm.subjectCode}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    mt={1}
                  >
                    <TextField
                      label={t("subject")}
                      value={sm.subjectName}
                      InputProps={{ readOnly: true }}
                      sx={{ flex: 2 }}
                    />
                    {form.marksheetType === "MONTHLY" ? (
                      // Monthly format - simple marks entry
                      <>
                        {(() => {
                          const monthlyMax = getCustomMaxMarks(
                            sm.subjectName,
                            "MONTHLY",
                            sm.subjectCode
                          ).theory;
                          return (
                            <>
                        <TextField
                          label={t("total_marks")}
                          type="number"
                          value={monthlyMax}
                          sx={{ flex: 2 }}
                          inputProps={{ min: 0, max: monthlyMax, readOnly: true }}
                        />
                        <TextField
                          label={t("marks_obtained")}
                          type="number"
                          value={sm.theoryMarks === null ? '' : sm.theoryMarks}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow empty string, numbers, and prevent non-numeric characters
                            // This will properly handle empty string vs '0' distinction
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              handleSubjectMarkChange(idx, "theoryMarks", value);
                            }
                          }}
                          sx={{
                            flex: 3,
                            '& .MuiInputBase-input': {
                              color: sm.theoryMarks === '' ? '#999' : 'inherit'
                            }
                          }}
                          inputProps={{
                            min: 0,
                            max: monthlyMax,
                            step: 1,
                          }}
                          error={!!formErrors[`theoryMarks_${idx}`]}
                          helperText={formErrors[`theoryMarks_${idx}`] || ''}
                          placeholder={t("enter_marks_optional")}
                        />
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      // Non-Monthly format - Theory/Practical marks entry
                      <>
                        {(() => {
                          const lowerBaseClass = baseClassName.toLowerCase();
                          const is6thOr7th = lowerBaseClass.includes("6th") || lowerBaseClass.includes("7th");
                          const isAnnual = form.marksheetType === "ANNUAL";
                          const isHalfYearly = form.marksheetType === "HALF_YEARLY";
                          const isClass3rd4th6th7thLocal =
                            lowerBaseClass.includes("3rd") ||
                            lowerBaseClass.includes("4th") ||
                            lowerBaseClass.includes("6th") ||
                            lowerBaseClass.includes("7th");
                          const customMax = getCustomMaxMarks(sm.subjectName, form.marksheetType, sm.subjectCode);

                          // 3/4/6/7 HALF_YEARLY: theory-only for non–462182; school 462182 uses theory + practical
                          if (
                            !isMainTemplateSchool &&
                            isClass3rd4th6th7thLocal &&
                            isHalfYearly
                          ) {
                            return (
                              <>
                                <TextField
                                  label={t("total_marks_theory")}
                                  type="number"
                                  value={customMax.theory}
                                  sx={{ flex: 2 }}
                                  inputProps={{ min: 0, readOnly: true }}
                                />
                                <TextField
                                  label={t("theory_marks_obtained")}
                                  type="number"
                                  value={sm.theoryMarks === null ? "" : sm.theoryMarks}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                      handleSubjectMarkChange(idx, "theoryMarks", value);
                                    }
                                  }}
                                  sx={{
                                    flex: 3,
                                    "& .MuiInputBase-input": {
                                      color: sm.theoryMarks === "" ? "#999" : "inherit",
                                    },
                                  }}
                                  inputProps={{
                                    min: 0,
                                    max: customMax.theory,
                                  }}
                                  error={!!formErrors[`theoryMarks_${idx}`]}
                                  helperText={formErrors[`theoryMarks_${idx}`] || ""}
                                  placeholder={t("enter_theory_marks_optional")}
                                />
                              </>
                            );
                          }

                          if (is6thOr7th && isAnnual) {
                            return (
                              <>
                                <TextField
                                  label={t("total_marks_theory")}
                                  type="number"
                                  value={customMax.theory}
                                  sx={{ flex: 1 }}
                                  inputProps={{ readOnly: true }}
                                />
                                <TextField
                                  label={t("total_marks_practical")}
                                  type="number"
                                  value={customMax.practical}
                                  sx={{ flex: 1 }}
                                  inputProps={{ readOnly: true }}
                                />
                                <TextField
                                  label={t("theory_marks_obtained")}
                                  type="number"
                                  value={sm.theoryMarks === null ? "" : sm.theoryMarks}
                                  onChange={(e) => handleSubjectMarkChange(idx, "theoryMarks", e.target.value)}
                                  sx={{
                                    flex: 1,
                                    "& .MuiInputBase-input": {
                                      color: sm.theoryMarks === "" ? "#999" : "inherit",
                                    },
                                  }}
                                  placeholder={
                                    isMainTemplateSchool
                                      ? "Original PR"
                                      : t("enter_theory_marks_optional")
                                  }
                                  error={!!formErrors[`theoryMarks_${idx}`]}
                                  helperText={formErrors[`theoryMarks_${idx}`] || ""}
                                />
                                <TextField
                                  label={t("practical_marks_obtained")}
                                  type="number"
                                  value={sm.practicalMarks === null ? "" : sm.practicalMarks}
                                  onChange={(e) => handleSubjectMarkChange(idx, "practicalMarks", e.target.value)}
                                  sx={{
                                    flex: 1,
                                    "& .MuiInputBase-input": {
                                      color: sm.practicalMarks === "" ? "#999" : "inherit",
                                    },
                                  }}
                                  placeholder={
                                    isMainTemplateSchool
                                      ? "Practical"
                                      : t("enter_practical_marks_optional")
                                  }
                                  error={!!formErrors[`practicalMarks_${idx}`]}
                                  helperText={formErrors[`practicalMarks_${idx}`] || ""}
                                />
                              </>
                            );
                          }

                          return (
                            <>
                              <TextField
                                label={t("total_marks_theory")}
                                type="number"
                                value={customMax.theory}
                                sx={{ flex: 1 }}
                                inputProps={{ min: 0, readOnly: true }}
                              />
                              <TextField
                                label={t("total_marks_practical")}
                                type="number"
                                value={customMax.practical}
                                sx={{ flex: 1 }}
                                inputProps={{ min: 0, readOnly: true }}
                              />
                              <TextField
                                label={t("theory_marks_obtained")}
                                type="number"
                                value={sm.theoryMarks === null ? "" : sm.theoryMarks}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                    handleSubjectMarkChange(idx, "theoryMarks", value);
                                  }
                                }}
                                sx={{
                                  flex: 1,
                                  "& .MuiInputBase-input": {
                                    color: sm.theoryMarks === "" ? "#999" : "inherit",
                                  },
                                }}
                                inputProps={{
                                  min: 0,
                                  max: customMax.theory,
                                }}
                                error={!!formErrors[`theoryMarks_${idx}`]}
                                helperText={formErrors[`theoryMarks_${idx}`] || ""}
                                placeholder={t("enter_theory_marks_optional")}
                              />
                              <TextField
                                label={t("practical_marks_obtained")}
                                type="number"
                                value={sm.practicalMarks === null ? "" : sm.practicalMarks}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                    handleSubjectMarkChange(idx, "practicalMarks", value);
                                  }
                                }}
                                sx={{
                                  flex: 1,
                                  "& .MuiInputBase-input": {
                                    color: sm.practicalMarks === "" ? "#999" : "inherit",
                                  },
                                }}
                                inputProps={{
                                  min: 0,
                                  max: customMax.practical,
                                }}
                                error={!!formErrors[`practicalMarks_${idx}`]}
                                helperText={formErrors[`practicalMarks_${idx}`] || ""}
                                placeholder={t("enter_practical_marks_optional")}
                              />
                            </>
                          );
                        })()}
                      </>
                    )}
                  </Box>
                ))
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    border: '2px dashed #dee2e6'
                  }}
                >
                  <SubjectIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t("no_subjects_available_for_class")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("please_ensure_subjects_configured")}
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>

          {shouldShowAssessmentGrades ? (
            <Card sx={{ mt: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <AssessmentIcon sx={{ color: "#3092e3" }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: "#3092e3" }}>
                    {t("assessment_grades") || "Assessment Grades"}
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                      <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <Typography fontWeight="bold">सह-शैक्षिक क्षेत्र</Typography>
                      </Box>

                      {coScholasticAssessments.map((row) => (
                        <Box
                          key={row.key}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            borderBottom: '1px solid #e2e8f0',
                          }}
                        >
                          <Typography sx={{ flex: 1 }}>{row.label}</Typography>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>ग्रेड</InputLabel>
                            <Select
                              label="ग्रेड"
                              value={(form.marksheet_assessment?.[row.key] as any) || ""}
                              onChange={(e: SelectChangeEvent) =>
                                setAssessmentGrade(row.key, e.target.value as GradeCategory)
                              }
                            >
                              {allowedAssessmentGradeOptions.map((opt) => (
                                <MenuItem key={String(opt.value)} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                      <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <Typography fontWeight="bold">व्यक्तित्व-सामाजिक गुण</Typography>
                      </Box>

                      {personalSocialAssessments.map((row) => (
                        <Box
                          key={row.key}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1.5,
                            borderBottom: '1px solid #e2e8f0',
                          }}
                        >
                          <Typography sx={{ flex: 1 }}>{row.label}</Typography>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>ग्रेड</InputLabel>
                            <Select
                              label="ग्रेड"
                              value={(form.marksheet_assessment?.[row.key] as any) || ""}
                              onChange={(e: SelectChangeEvent) =>
                                setAssessmentGrade(row.key, e.target.value as GradeCategory)
                              }
                            >
                              {allowedAssessmentGradeOptions.map((opt) => (
                                <MenuItem key={String(opt.value)} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={handleModalClose}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderColor: '#f44336',
              color: '#f44336',
              '&:hover': {
                borderColor: '#d32f2f',
                color: '#d32f2f',
                backgroundColor: 'rgba(244, 67, 54, 0.04)',
              }
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={filteredStudents.length === 0 || isSubmitting}
            sx={{
              minWidth: 100,
              backgroundColor: '#3092e3',
              '&:hover': {
                backgroundColor: 'primary.main',
              },
              '&.Mui-disabled': {
                backgroundColor: '#b2ebff',
                color: '#fff',
              }
            }}
          >
            {isSubmitting ? t("processing") : (isEdit ? t("update") : t("add"))}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MarkSheetFormModal;
